/**
 * aspectApi.ts
 *
 * Aspect creation (EAINT-933 Phase 3.4, "Draft a new aspect" — `blank`/`extend` routes). Kept
 * out of `schemaApi.ts` (reads only) and `updateSphereType.ts` (the schema's own field patch):
 * `POST /aspects/` is the aspect service's own write, registering a new template in its own
 * right, not a change to the composing type's `Schema.aspects` — attaching the created aspect
 * to the type is still a separate `aspects` field patch through `updateSphereType`.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { AspectDefinition } from 'types/aspect';
import type { LocalizedString } from 'types/context';

export interface CreateAspectInput {
  systemName: string;
  name: LocalizedString;
  definition: AspectDefinition;
}

/**
 * `createAspect.ts`'s 409 — the `system_name` is already registered to another template IN
 * THIS ORGANIZATION (uniqueness is per-org, not global; `aspectHelpers.ts`). The dialog
 * surfaces this inline and keeps the draft open rather than losing what the author typed
 * (spec `draftAspectCollisionKeepsTheDraft`).
 */
export class AspectSystemNameCollisionError extends Error {
  readonly systemName: string;

  constructor(systemName: string) {
    super(`system_name '${systemName}' is already registered`);
    this.name = 'AspectSystemNameCollisionError';
    this.systemName = systemName;
  }
}

function statusOf(err: unknown): number | undefined {
  const anyErr = err as { response?: { status?: number }; status?: number } | undefined;
  return anyErr?.response?.status ?? anyErr?.status;
}

/**
 * The platform's aspect `system_name` convention is underscore-joined, not kebab
 * (`types/aspect.d.ts` `AspectDefinition.system_name` doc: `center_revenue`,
 * `acme_food_safety_score`) — unlike `generateSystemName` in schema-service's context
 * helpers, which produces kebab-case for a different template kind.
 */
export function slugifyAspectName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replaceAll(/[^a-z0-9\s_]/g, '')
    .replaceAll(/[\s_]+/g, '_')
    .replaceAll(/(^_+)|(_+$)/g, '');
}

/**
 * `POST /aspects/` (`createAspect.ts`), owned by the active organization. Aspect creation has no
 * server-side namespacing of its own, so `input.systemName` must already be the FULL intended
 * `system_name` — this function does not slug or prefix it (see `slugifyAspectName`).
 */
export async function createAspect(schemaClient: ApiClient, input: CreateAspectInput): Promise<{ systemName: string }> {
  try {
    await schemaClient.post('/aspects/', {
      systemName: input.systemName,
      name: input.name,
      definition: input.definition,
    });
    return { systemName: input.systemName };
  } catch (err) {
    if (statusOf(err) === 409) throw new AspectSystemNameCollisionError(input.systemName);
    throw err;
  }
}
